import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  makeStyles,
} from "@fluentui/react-components";
import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { ModalSubmitText, ModalText, ModalTitle, ModalType } from "../common/commonConstants";
import { closeModal, deleteMenu, deleteMenuItem, deleteStation, publishMenu } from "../reducers/diningReducer";
import { currentModalSelector } from "../selectors/diningSelector";
import { useAppDispatch } from "../store/store";
import withAppInsights from "./AppInsights/AppInsights";

/**
 * Hook for trigger components to restore focus when a dialog closes.
 * Uses a useRef internally — no module-level or global state.
 *
 * Captures the active element synchronously in onClick (via the returned
 * function), then restores focus after the Fluent UI DialogSurface is
 * removed from the DOM (to act after tabster's own focus restoration).
 *
 * @param modalType - The ModalType this trigger is associated with.
 * @returns A capture function to call synchronously in onClick.
 *          Optionally pass an explicit HTMLElement; defaults to document.activeElement.
 */
export const useRestoreFocusOnDialogClose = (modalType: ModalType) => {
  const triggerRef = useRef<HTMLElement | null>(null);
  const currentModal = useSelector(currentModalSelector);

  const captureTrigger = (explicitElement?: HTMLElement | null) => {
    triggerRef.current = explicitElement ?? (document.activeElement as HTMLElement);
  };

  useEffect(() => {
    // Only act when our specific modal just closed
    if (currentModal || !triggerRef.current) return;

    const savedElement = triggerRef.current;

    const observer = new MutationObserver(() => {
      if (!document.querySelector(".fui-DialogSurface")) {
        observer.disconnect();
        if (document.body.contains(savedElement)) {
          savedElement.focus();
        }
        triggerRef.current = null;
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    // Fallback: if the dialog is already gone, focus immediately
    if (!document.querySelector(".fui-DialogSurface")) {
      observer.disconnect();
      if (document.body.contains(savedElement)) {
        savedElement.focus();
      }
      triggerRef.current = null;
    }

    return () => observer.disconnect();
  }, [currentModal]);

  return captureTrigger;
};

const useDialogStyles = makeStyles({
  surface: {
    width: "440px",
    maxWidth: "90vw",
    minWidth: "288px",
  },
  content: {
    color: "#323130",
    marginBottom: "24px",
  },
});
export const DialogBox = () => {
  const modalContent = useSelector(currentModalSelector);
  const styles = useDialogStyles();
  const dispatch = useAppDispatch();
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  // Move keyboard focus into the dialog when it opens. The portal content may not be
  // attached yet when Fluent's own first-focus logic runs, so verify and fall back.
  useEffect(() => {
    if (!modalContent) return;

    const focusFirstElement = () => {
      const surface = surfaceRef.current;
      const activeElement = document.activeElement;
      if (
        activeElement &&
        surface?.contains(activeElement) &&
        activeElement !== surface &&
        !activeElement.hasAttribute("data-tabster-dummy")
      ) {
        return;
      }
      closeButtonRef.current?.focus();
    };

    const frame = requestAnimationFrame(focusFirstElement);
    const timer = setTimeout(focusFirstElement, 100);

    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer);
    };
  }, [modalContent]);

  // Disable the CoherenceHeader Panel's focus trap when a dialog is open.
  useEffect(() => {
    if (!modalContent) return;

    const panel = document.querySelector(".ms-Panel") as HTMLElement | null;
    if (panel) {
      panel.setAttribute("inert", "");
    }

    return () => {
      if (panel) {
        panel.removeAttribute("inert");
      }
    };
  }, [modalContent]);

  let submitButtonText = "";
  let submitFunction = () => {};
  let footer = false;

  const confirmStationDelete = () => {
    dispatch(closeModal());
    dispatch(deleteStation());
  };

  const confirmMenuItemDelete = () => {
    dispatch(closeModal());
    dispatch(deleteMenuItem());
  };

  const confirmPublishMenu = () => {
    dispatch(closeModal());
    dispatch(publishMenu());
  };

  const confirmDeleteMenu = () => {
    dispatch(closeModal());
    dispatch(deleteMenu());
  };

  const dismissModal = () => {
    dispatch(closeModal());
  };

  switch (modalContent) {
    case ModalType.CONFIRM_DELETE_STATION:
      submitButtonText = ModalSubmitText[modalContent];
      submitFunction = confirmStationDelete;
      footer = true;
      break;
    case ModalType.CONFIRM_MENU_ITEM_DELETE:
      submitButtonText = ModalSubmitText[modalContent];
      submitFunction = confirmMenuItemDelete;
      footer = true;
      break;
    case ModalType.CONFIRM_PUBLISH_MENU:
      submitButtonText = ModalSubmitText[modalContent];
      submitFunction = confirmPublishMenu;
      footer = true;
      break;
    case ModalType.CONFIRM_DELETE_MENU:
      submitButtonText = ModalSubmitText[modalContent];
      submitFunction = confirmDeleteMenu;
      footer = true;
      break;
    default:
      submitFunction = () => {
        return;
      };
      footer = false;
  }

  return (
    // @ts-ignore
    <Dialog open={!!modalContent} onOpenChange={dismissModal}>
      <DialogSurface className={styles.surface} ref={surfaceRef}>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button
                  ref={closeButtonRef}
                  appearance="subtle"
                  aria-label="Close"
                  style={{ backgroundColor: "transparent !important" }}
                  autoFocus
                >
                  ✕
                </Button>
              </DialogTrigger>
            }
          >
            {modalContent ? ModalTitle[modalContent] : ""}
          </DialogTitle>

          {modalContent && <DialogContent className={styles.content}>{ModalText[modalContent]}</DialogContent>}

          {footer && (
            <DialogActions>
              <Button appearance="primary" onClick={submitFunction}>
                {submitButtonText}
              </Button>
              <Button onClick={dismissModal}>Cancel</Button>
            </DialogActions>
          )}
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default withAppInsights(DialogBox);
